/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  calculateMargin,
  calculatePriceFromMargin,
} from "@/lib/utils/domain/calculations";
import { calcTotalCost } from "@/lib/utils/domain/calculation-helpers";
import { logger } from "@/lib/utils/logger";

type PricedLineItem = {
  unitCost?: number | null;
  quantity?: number | null;
  unitPrice?: number | null;
  totalCost?: number | null;
  totalPrice?: number | null;
  /** Stores margin (% of price) despite the legacy field name */
  marginPercentage?: number | null;
  /** Stores margin (% of price) despite the legacy field name */
  profitPercentage?: number | null;
  workAreaId?: string | null;
};

/**
 * Calculate margin percentage from cost and price
 * Formula: margin = ((price - cost) / price) * 100
 */
export function calculateMarginFromPrice(
  unitCost: number,
  unitPrice: number,
): number {
  if (!unitPrice || unitPrice <= 0) return 0;
  return calculateMargin(unitCost, unitPrice);
}

/**
 * Calculate price from cost and margin percentage
 * Formula: price = cost / (1 - margin/100)
 */
export function calculatePriceFromMarginPercent(
  unitCost: number,
  marginPercentage: number,
): number {
  if (!unitCost || unitCost <= 0) return 0;
  return calculatePriceFromMargin(unitCost, marginPercentage);
}

/**
 * Calculate total price from quantity, unit cost, and margin
 */
export function calculateTotalPrice(
  quantity: number,
  unitCost: number,
  marginPercentage: number,
): number {
  const unitPrice = calculatePriceFromMarginPercent(unitCost, marginPercentage);
  return quantity * unitPrice;
}

function calculateTotalCost(quantity: number, unitCost: number): number {
  return calcTotalCost(quantity, unitCost);
}

/**
 * Update line item when price changes - keeps cost the same and recalculates margin%
 * Requirement: When price changes → keep cost the same, change margin %
 */
export function updateLineItemFromPrice<T extends PricedLineItem>(
  item: T,
  newUnitPrice: number,
): Partial<T> {
  const unitCost = item.unitCost ?? 0;
  const quantity = item.quantity ?? 0;
  const totalCost = calculateTotalCost(quantity, unitCost);
  const marginPercentage = calculateMarginFromPrice(unitCost, newUnitPrice);
  const totalPrice = newUnitPrice * quantity;

  return {
    unitCost,
    totalCost,
    unitPrice: newUnitPrice,
    totalPrice,
    marginPercentage: marginPercentage,
    profitPercentage: marginPercentage,
  } as Partial<T>;
}

/**
 * Update line item when margin% changes - keeps cost the same and recalculates price
 * Requirement: When margin% changes → keep cost the same, change price
 */
export function updateLineItemFromMargin<T extends PricedLineItem>(
  item: T,
  newMarginPercentage: number,
): Partial<T> {
  const unitCost = item.unitCost ?? 0;
  const quantity = item.quantity ?? 0;
  const totalCost = calculateTotalCost(quantity, unitCost);
  const unitPrice = calculatePriceFromMarginPercent(
    unitCost,
    newMarginPercentage,
  );
  const totalPrice = unitPrice * quantity;

  return {
    unitCost,
    totalCost,
    unitPrice,
    totalPrice,
    marginPercentage: newMarginPercentage,
    profitPercentage: newMarginPercentage,
  } as Partial<T>;
}

/**
 * Update line item when cost changes - keeps price the same and recalculates margin%
 * Requirement: When cost changes → keep price the same, change margin %
 */
export function updateLineItemFromCost<T extends PricedLineItem>(
  item: T,
  newUnitCost: number,
): Partial<T> {
  const quantity = item.quantity ?? 0;
  const totalCost = calculateTotalCost(quantity, newUnitCost);

  if (item.unitPrice !== undefined && item.unitPrice !== null) {
    const marginPercentage = calculateMarginFromPrice(
      newUnitCost,
      item.unitPrice,
    );
    const totalPrice = item.unitPrice * quantity;

    return {
      unitCost: newUnitCost,
      totalCost,
      unitPrice: item.unitPrice,
      totalPrice,
      marginPercentage: marginPercentage,
      profitPercentage: marginPercentage,
    } as Partial<T>;
  }

  if (item.marginPercentage !== undefined && item.marginPercentage !== null) {
    const unitPrice = calculatePriceFromMarginPercent(
      newUnitCost,
      item.marginPercentage,
    );
    const totalPrice = calculateTotalPrice(
      quantity,
      newUnitCost,
      item.marginPercentage,
    );

    return {
      unitCost: newUnitCost,
      totalCost,
      unitPrice,
      totalPrice,
      marginPercentage: item.marginPercentage,
      profitPercentage: item.marginPercentage,
    } as Partial<T>;
  }

  return {
    unitCost: newUnitCost,
    totalCost,
  } as Partial<T>;
}

/**
 * Update line item when quantity changes - recalculates totals
 */
export function updateLineItemFromQuantity<T extends PricedLineItem>(
  item: T,
  newQuantity: number,
): Partial<T> {
  const unitCost = item.unitCost ?? 0;
  const totalCost = calculateTotalCost(newQuantity, unitCost);

  if (item.marginPercentage !== undefined && item.marginPercentage !== null) {
    const totalPrice = calculateTotalPrice(
      newQuantity,
      unitCost,
      item.marginPercentage,
    );
    return {
      quantity: newQuantity,
      totalCost,
      totalPrice,
    } as Partial<T>;
  }

  if (item.unitPrice !== undefined && item.unitPrice !== null) {
    const totalPrice = newQuantity * item.unitPrice;
    return {
      quantity: newQuantity,
      totalCost,
      totalPrice,
    } as Partial<T>;
  }

  return {
    quantity: newQuantity,
    totalCost,
  } as Partial<T>;
}

/**
 * Calculate work area totals for both costs and prices
 */
export function calculateWorkAreaTotals<T extends PricedLineItem>(
  lineItems: T[],
  workAreaId: string,
): { totalCost: number; totalPrice: number } {
  const workAreaItems = lineItems.filter(
    (item) => item.workAreaId === workAreaId,
  );

  const totalCost = workAreaItems.reduce(
    (sum, item) => sum + (item.totalCost ?? 0),
    0,
  );

  const totalPrice = workAreaItems.reduce((sum, item) => {
    if (item.totalPrice == null && item.totalCost != null) {
      logger.warn(
        "Work area line item missing totalPrice, falling back to totalCost (margin lost)",
        {
          workAreaId,
          totalCost: item.totalCost,
        },
      );
    }
    return sum + (item.totalPrice ?? item.totalCost ?? 0);
  }, 0);

  return { totalCost, totalPrice };
}

/**
 * Get total cost for a work area
 */
export function getWorkAreaTotal<T extends PricedLineItem>(
  lineItems: T[],
  workAreaId: string,
): number {
  return calculateWorkAreaTotals(lineItems, workAreaId).totalCost;
}

/**
 * Get unassigned line items (items without a work area)
 */
export function getUnassignedLineItems<T extends PricedLineItem>(
  lineItems: T[],
): T[] {
  return lineItems.filter((item) => !item.workAreaId || item.workAreaId === "");
}
